/**
 * Renders an identity-provider logo from the ``logo`` string stored on the
 * server. Two formats are accepted:
 *   - ``preset:<key>`` — picks one of the bundled inline SVGs below.
 *   - any other non-empty value — treated as an image URL and rendered with
 *     ``<img>``.
 *   - empty — falls back to a generic shield icon.
 */

import { ShieldCheck } from 'lucide-react'
import type { ReactElement } from 'react'

const Google = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
    <path
      fill="#FFC107"
      d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8a12 12 0 1 1 7.9-21l5.7-5.6A20 20 0 1 0 44 24c0-1.2-.1-2.4-.4-3.5z"
    />
    <path
      fill="#FF3D00"
      d="M6.3 14.1l6.6 4.8C14.6 15.1 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.6A20 20 0 0 0 6.3 14.1z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5 0 9.7-1.9 13.2-5.1l-6.1-5.2A12 12 0 0 1 12.7 28l-6.6 5.1A20 20 0 0 0 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4.1 5.7l6.1 5.2c-.4.4 6.7-4.9 6.7-14.9 0-1.2-.1-2.4-.4-3.5z"
    />
  </svg>
)

const Microsoft = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <path fill="#F25022" d="M11 11H1V1h10z" />
    <path fill="#7FBA00" d="M23 11H13V1h10z" />
    <path fill="#00A4EF" d="M11 23H1V13h10z" />
    <path fill="#FFB900" d="M23 23H13V13h10z" />
  </svg>
)

const Apple = ({
  size,
  fill = 'currentColor'
}: {
  size: number
  fill?: string
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden fill={fill}>
    <path d="M16.36 12.66c0-2.41 1.97-3.57 2.06-3.62-1.12-1.64-2.87-1.86-3.5-1.89-1.49-.15-2.9.88-3.65.88-.76 0-1.92-.86-3.16-.83-1.62.02-3.13.94-3.97 2.39-1.7 2.94-.43 7.27 1.21 9.65.81 1.16 1.76 2.46 3.01 2.42 1.21-.05 1.66-.78 3.12-.78 1.46 0 1.87.78 3.15.76 1.3-.02 2.13-1.18 2.92-2.35.92-1.34 1.3-2.65 1.32-2.72-.03-.01-2.51-.97-2.51-3.91zM14.07 5.6c.66-.81 1.11-1.93.99-3.05-.96.04-2.13.64-2.81 1.45-.61.71-1.15 1.86-1 2.95 1.07.08 2.16-.54 2.82-1.35z" />
  </svg>
)

const GitHub = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <path
      fill="currentColor"
      d="M12 .5C5.7.5.5 5.7.5 12c0 5.1 3.3 9.4 7.8 10.9.6.1.8-.2.8-.6v-2c-3.2.7-3.9-1.5-3.9-1.5-.5-1.3-1.3-1.7-1.3-1.7-1.1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1 1.8 2.8 1.3 3.5 1 .1-.8.4-1.3.7-1.6-2.6-.3-5.3-1.3-5.3-5.7 0-1.3.4-2.3 1.2-3.1-.1-.3-.5-1.5.1-3.1 0 0 1-.3 3.3 1.2.9-.3 1.9-.4 2.9-.4s2 .1 2.9.4c2.3-1.5 3.3-1.2 3.3-1.2.6 1.6.2 2.8.1 3.1.8.8 1.2 1.9 1.2 3.1 0 4.4-2.7 5.4-5.3 5.7.4.4.8 1.1.8 2.2v3.3c0 .3.2.7.8.6 4.5-1.5 7.8-5.8 7.8-10.9C23.5 5.7 18.3.5 12 .5z"
    />
  </svg>
)

const GitLab = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <path
      fill="#FC6D26"
      d="M23.6 9.6 23.6 9.6 20.7.6c-.1-.4-.5-.7-.9-.7-.5 0-.9.3-1 .8L16 9.4H8L5.2.7C5.1.3 4.7 0 4.2 0c-.4 0-.8.3-.9.7L.4 9.6h0a2 2 0 0 0 .7 2.3l10.6 7.7c.2.1.4.1.6 0L22.9 11.9a2 2 0 0 0 .7-2.3z"
    />
  </svg>
)

const Okta = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <path
      fill="#007DC1"
      d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 14.7a4.7 4.7 0 1 1 0-9.4 4.7 4.7 0 0 1 0 9.4z"
    />
  </svg>
)

const Auth0 = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden
    fill="#EB5424"
  >
    <path d="M21.98 7.448L19.62 0H4.38L2.02 7.448c-1.38 4.354.18 9.354 3.96 12.31L12 24l6.02-4.243c3.78-2.956 5.34-7.956 3.96-12.31zm-13.45 9.412L6.16 9.554l5.84-4.286 5.84 4.286-2.37 7.306-3.47-2.546-3.47 2.546z" />
  </svg>
)

const PRESETS: Record<string, (props: { size: number }) => ReactElement> = {
  google: Google,
  microsoft: Microsoft,
  apple: Apple,
  github: GitHub,
  gitlab: GitLab,
  okta: Okta,
  auth0: Auth0
}

export const PROVIDER_LOGO_PRESETS = Object.keys(PRESETS) as ReadonlyArray<
  keyof typeof PRESETS
>

export const presetValue = (key: string) => `preset:${key}`

export const ProviderLogo = ({
  value,
  size = 20
}: {
  value: string | null | undefined
  size?: number
}) => {
  if (!value) {
    return <ShieldCheck size={size} />
  }
  if (value.startsWith('preset:')) {
    const key = value.slice('preset:'.length)
    const Comp = PRESETS[key]
    if (Comp) {
      return <Comp size={size} />
    }
  }
  return (
    <img
      src={value}
      alt=""
      style={{ width: size, height: size, objectFit: 'contain' }}
    />
  )
}
